import React from 'react';

import { css } from 'styled-components';

import useHasFeature, { featureNames } from '~/shared/utils/useHasFeature';
import { Video } from '~/techstyle-shared/react-components';

import { DEFAULT_VIDEO_ASPECT_RATIO } from '../../constants';
import useAssetCardTracking from '../../hooks/useAssetCardTracking';
import { getVimeoUrlFromVideoId } from '../../utils/video';

import AssetCardFallback from './AssetCardFallback';
import AssetCardImage from './AssetCardImage';
import AssetCardVideo from './AssetCardVideo';

const LegacyVideo = Video as React.ComponentType<any>;

const legacyVideoWrapperStyle = css`
  width: 100%;
  height: 100%;

  &:before {
    padding-top: 0;
  }
`;

// When the video owns the full grid row there are no product cards to supply row
// height, so height: 100% collapses to 0. Keep the shared Video component's built-in
// padding-top aspect-ratio trick instead of overriding it to 0, so the wrapper
// establishes intrinsic height on its own — mirrors how <img> naturally takes space.
const legacyVideoFullRowWrapperStyle = css`
  width: 100%;
`;

interface AssetCardOptions {
  customVars?: {
    videoId?: string;
    VideoID?: string;
    videoAspectRatio?: number;
    // eslint-disable-next-line camelcase
    clickable_asset_extra_properties?: string;
  };
  targetUrl?: string;
}

interface AssetCardProps {
  imageFilename?: string;
  label?: string;
  options?: AssetCardOptions;
  className?: string;
  container?: {
    name?: string;
  };
  isFullRow?: boolean;
}

// className is necessary for forwarding styles, e.g. for `styled(AssetCard)`
const AssetCard = ({
  imageFilename,
  label,
  options,
  className,
  container,
  isFullRow = false,
}: AssetCardProps) => {
  const { imageTrackingData, trackAssetClick } = useAssetCardTracking({
    imageFilename,
    label,
    options,
    container,
  });

  const { enabled: isFullHeightVideoEnabled } = useHasFeature(
    featureNames.GRID_V2_FULL_HEIGHT_VIDEO
  );

  if (imageFilename) {
    return (
      <AssetCardImage
        imageFilename={imageFilename}
        label={label}
        options={options}
        className={className}
        imageTrackingData={imageTrackingData}
        trackAssetClick={trackAssetClick}
      />
    );
  }

  const videoId = options?.customVars?.videoId || options?.customVars?.VideoID;
  const videoUrl = getVimeoUrlFromVideoId(videoId);
  const aspectRatio =
    options?.customVars?.videoAspectRatio || DEFAULT_VIDEO_ASPECT_RATIO;

  if (videoId) {
    return isFullHeightVideoEnabled ? (
      <AssetCardVideo
        url={videoUrl}
        aspectRatio={aspectRatio}
        isFullRow={isFullRow}
      />
    ) : (
      <LegacyVideo
        url={videoUrl}
        aspectRatio={aspectRatio}
        wrapperStyle={
          isFullRow ? legacyVideoFullRowWrapperStyle : legacyVideoWrapperStyle
        }
      />
    );
  }

  return <AssetCardFallback />;
};

export default AssetCard;
